/**
 * Snippet builders + illustrative literals for the integration docs
 * page (`/access/docs`, epic #897 PR 5; SKU naming since epic #898).
 *
 * Code snippets are deliberately NOT translated — they are code. Their
 * surrounding prose lives in the i18n dictionaries. Model names shown
 * in prose and snippets are read from the live catalog SKU market where
 * available; the literals below are the single home for the genuinely
 * illustrative fallbacks (while the catalog loads), so example drift
 * has exactly one place to fix. All model literals use the canonical
 * `namespace/model` form — bare names only resolve through per-key
 * model mappings and would 404 on a key without one.
 */

export type DocsPlatform = 'macos' | 'windows';
export type DocsProtocol = 'anthropic' | 'openai';

/** Placeholder key shown inside snippets — never a real credential. */
export const KEY_PLACEHOLDER = 'sk-ndt-你的密钥';

/**
 * Placeholder model for the verify-section /v1/messages curl. The SPA
 * cannot know which Claude namespace accepts plain-API (non-Claude-Code)
 * traffic — that policy is not in the catalog — so the example tells the
 * tenant to substitute a name from their own GET /v1/models instead of
 * baking in a model that may be Claude Code-only and 5xx under curl.
 */
export const MODEL_PLACEHOLDER = '命名空间/模型名';

/** Illustrative canonical `namespace/model` ids, fallback while the catalog loads. */
export const EXAMPLE_ANTHROPIC_MODEL = 'oauth/claude-sonnet-4-6';
export const EXAMPLE_OPENAI_MODEL = 'codex/gpt-5.4';

/**
 * Fallback model lists for prose, used only while the catalog query
 * has no data (loading / error). Canonical `namespace/model` names.
 */
export const FALLBACK_ANTHROPIC_MODELS: readonly string[] = [
  'oauth/claude-opus-4-7',
  'oauth/claude-sonnet-4-6',
  'oauth/claude-haiku-4-5-20251001',
  'kiro/claude-opus-4-8',
];

export const FALLBACK_OPENAI_MODELS: readonly string[] = [
  'codex/gpt-5.5',
  'codex/gpt-5.4',
];

export const ENV_CHECK_SNIPPET = 'node -v\nnpm -v';

export const CLAUDE_CODE_INSTALL_SNIPPET =
  'npm install -g @anthropic-ai/claude-code\nclaude --version';

export const CC_SWITCH_INSTALL_SNIPPET =
  'brew tap farion1231/ccswitch\nbrew install --cask cc-switch';

export function modelsCurlSnippet(anthropicBase: string): string {
  return `curl ${anthropicBase}/v1/models \\\n  -H "Authorization: Bearer ${KEY_PLACEHOLDER}"`;
}

export function claudeTempEnvSnippet(
  platform: DocsPlatform,
  anthropicBase: string,
  model: string,
): string {
  if (platform === 'windows') {
    return `$env:ANTHROPIC_BASE_URL = "${anthropicBase}"\n$env:ANTHROPIC_AUTH_TOKEN = "${KEY_PLACEHOLDER}"\n$env:ANTHROPIC_MODEL = "${model}"\nclaude`;
  }
  return `export ANTHROPIC_BASE_URL="${anthropicBase}"\nexport ANTHROPIC_AUTH_TOKEN="${KEY_PLACEHOLDER}"\nexport ANTHROPIC_MODEL="${model}"\nclaude`;
}

export function claudeLongTermSnippet(
  platform: DocsPlatform,
  anthropicBase: string,
  model: string,
): string {
  if (platform === 'windows') {
    return `[Environment]::SetEnvironmentVariable("ANTHROPIC_BASE_URL", "${anthropicBase}", "User")\n[Environment]::SetEnvironmentVariable("ANTHROPIC_AUTH_TOKEN", "${KEY_PLACEHOLDER}", "User")\n[Environment]::SetEnvironmentVariable("ANTHROPIC_MODEL", "${model}", "User")`;
  }
  return `echo 'export ANTHROPIC_BASE_URL="${anthropicBase}"' >> ~/.zshrc\necho 'export ANTHROPIC_AUTH_TOKEN="${KEY_PLACEHOLDER}"' >> ~/.zshrc\necho 'export ANTHROPIC_MODEL="${model}"' >> ~/.zshrc\nsource ~/.zshrc`;
}

export function disableClaudeBetasSnippet(platform: DocsPlatform): string {
  if (platform === 'windows') {
    return '$env:CLAUDE_CODE_DISABLE_EXPERIMENTAL_BETAS = "1"\nclaude';
  }
  return 'export CLAUDE_CODE_DISABLE_EXPERIMENTAL_BETAS=1\nclaude';
}

export function codexEnvSnippet(platform: DocsPlatform, openaiBase: string): string {
  if (platform === 'windows') {
    return `$env:OPENAI_BASE_URL = "${openaiBase}"\n$env:OPENAI_API_KEY = "${KEY_PLACEHOLDER}"\ncodex`;
  }
  return `export OPENAI_BASE_URL="${openaiBase}"\nexport OPENAI_API_KEY="${KEY_PLACEHOLDER}"\ncodex`;
}

export function codexConfigSnippet(openaiBase: string, model: string): string {
  return `model = "${model}"\nmodel_provider = "nadoutong"\n\n[model_providers.nadoutong]\nname = "nadoutong"\nbase_url = "${openaiBase}"\nenv_key = "OPENAI_API_KEY"\nwire_api = "responses"`;
}

export function openclawProviderSnippet(openaiBase: string, model: string): string {
  return `provider: openai-compatible\nbase_url: "${openaiBase}"\napi_key: "${KEY_PLACEHOLDER}"\nmodel: "${model}"`;
}

export function lobechatEnvSnippet(openaiBase: string): string {
  return `OPENAI_API_KEY=${KEY_PLACEHOLDER}\nOPENAI_PROXY_URL=${openaiBase}`;
}

export function anthropicCurlSnippet(anthropicBase: string): string {
  return `curl ${anthropicBase}/v1/messages \\\n  -H "Authorization: Bearer ${KEY_PLACEHOLDER}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${MODEL_PLACEHOLDER}","max_tokens":1024,"messages":[{"role":"user","content":"hi"}]}'`;
}

export function openaiCurlSnippet(openaiBase: string, model: string): string {
  return `curl ${openaiBase}/chat/completions \\\n  -H "Authorization: Bearer ${KEY_PLACEHOLDER}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${model}","messages":[{"role":"user","content":"hi"}]}'`;
}

/**
 * Illustrative image SKU, fallback while the catalog loads or when it holds
 * no image SKU at all. Never rendered when the tenant actually has one — the
 * page prefers the live catalog name.
 *
 * The namespace here follows the same "plausible, illustrative" convention as
 * the literals above; it is NOT a promise about where the image SKU ships.
 * If the image line lands under a different namespace, update this with it,
 * so a reader who copies the snippet at least sees a name shaped like the one
 * they will be given.
 */
export const EXAMPLE_IMAGE_MODEL = 'codex/gpt-image-2';

/**
 * Official SDK, unchanged from upstream usage apart from `base_url`. That is
 * the whole point of this ingress: `client.images.generate(...)` and
 * `client.images.edit(...)` work as written in the OpenAI documentation.
 *
 * Both calls live in one block on purpose. They share the client, the model
 * and the decode step, so splitting them made the reader diff two snippets to
 * find the one line that differs; together, the difference is the snippet.
 */
export function imagesSdkSnippet(openaiBase: string, model: string): string {
  return [
    'from openai import OpenAI',
    'import base64',
    '',
    `client = OpenAI(api_key="${KEY_PLACEHOLDER}", base_url="${openaiBase}", timeout=600)`,
    '',
    '# Generate',
    'result = client.images.generate(',
    `    model="${model}",`,
    '    prompt="A red bicycle leaning against a plain wall",',
    ')',
    'open("out.png", "wb").write(base64.b64decode(result.data[0].b64_json))',
    '',
    '# Edit',
    'edited = client.images.edit(',
    `    model="${model}",`,
    '    prompt="Make the bicycle blue, leave everything else unchanged",',
    '    image=open("out.png", "rb"),',
    ')',
    'open("edited.png", "wb").write(base64.b64decode(edited.data[0].b64_json))',
  ].join('\n');
}

/** The same two calls over curl; `edits` takes multipart, as upstream does. */
export function imagesCurlBothSnippet(openaiBase: string, model: string): string {
  return [
    '# Generate',
    `curl ${openaiBase}/images/generations \\`,
    `  -H "Authorization: Bearer ${KEY_PLACEHOLDER}" \\`,
    '  -H "Content-Type: application/json" \\',
    `  -d '{"model":"${model}","prompt":"A red bicycle leaning against a plain wall"}'`,
    '',
    '# Edit (multipart)',
    `curl ${openaiBase}/images/edits \\`,
    `  -H "Authorization: Bearer ${KEY_PLACEHOLDER}" \\`,
    `  -F model="${model}" \\`,
    '  -F prompt="Make the bicycle blue, leave everything else unchanged" \\',
    '  -F image=@out.png',
  ].join('\n');
}
